import { useEffect, useState } from "react";
import { Link } from "wouter";
import type { User } from "../lib/types";
import { COURSES } from "../data/courses";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { I } from "../components/Icons";
import { STEP_TOTALS, overallPct, pct } from "../lib/step-totals";

interface LearnerProgress {
  userId: string;
  name: string;
  email: string;
  role: string;
  progress: {
    steps: Record<string, string[]>;
    quiz: Record<string, number>;
    completed?: Record<string, boolean>;
  };
}

interface Props {
  user: User;
  onSignOut: () => void;
}

export function Admin({ user, onSignOut }: Props) {
  const [learners, setLearners] = useState<LearnerProgress[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/admin/progress", { credentials: "include" });
        if (!res.ok) {
          if (alive) setError(res.status === 403 ? "You don't have access to this page." : `Failed to load (${res.status}).`);
          return;
        }
        const data = (await res.json()) as { learners: LearnerProgress[] };
        if (alive) setLearners(data.learners);
      } catch {
        if (alive) setError("Couldn't reach the server.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Live updates: the server pushes a learner's full progress over the presence
  // socket (admin-only feed) whenever they sync, so this table reflects course
  // completions in real time without a manual refresh.
  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;
    let reconnect = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      if (cancelled) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${proto}://${window.location.host}/ws/presence`);

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type !== "progress" || !msg.learner) return;
          const incoming = msg.learner as LearnerProgress;
          setLearners((prev) => {
            const list = prev ?? [];
            const idx = list.findIndex((l) => l.userId === incoming.userId);
            if (idx === -1) return [...list, incoming];
            const next = list.slice();
            next[idx] = incoming;
            return next;
          });
        } catch {
          // ignore malformed frames
        }
      };

      ws.onopen = () => {
        reconnect = 0;
      };

      ws.onclose = () => {
        if (cancelled) return;
        const delay = Math.min(10_000, 500 * 2 ** reconnect++);
        timer = setTimeout(connect, delay);
      };

      ws.onerror = () => ws?.close();
    }

    connect();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, []);

  return (
    <div className="gf-shell">
      <Sidebar active="admin" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "Team progress", strong: true },
          ]}
        />
        <div className="gf-dash">
          <div className="gf-dash__section-head">
            <div>
              <h2>Team progress</h2>
              <p>
                Every learner's progress across the six courses. Completion is the
                share of lesson steps finished; quiz shows their best score.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Link href="/admin/courses" className="gf-btn gf-btn--secondary">
                <I.Lock size={15} /> Manage courses
              </Link>
              <Link href="/admin/users" className="gf-btn gf-btn--secondary">
                <I.Users size={15} /> Manage users
              </Link>
            </div>
          </div>

          {error ? (
            <div className="gf-card" style={{ padding: "var(--space-5)", color: "var(--danger)" }}>
              {error}
            </div>
          ) : !learners ? (
            <div className="gf-card" style={{ padding: "var(--space-5)", color: "var(--fg-3)" }}>
              Loading…
            </div>
          ) : learners.length === 0 ? (
            <div className="gf-card" style={{ padding: "var(--space-5)", color: "var(--fg-3)" }}>
              No learners have signed in yet.
            </div>
          ) : (
            <ProgressTable learners={learners} />
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressTable({ learners }: { learners: LearnerProgress[] }) {
  const sorted = [...learners].sort(
    (a, b) => overallPct(b.progress) - overallPct(a.progress),
  );

  const th: React.CSSProperties = {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: "var(--fs-caption)",
    color: "var(--fg-3)",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "var(--ls-wide)",
    borderBottom: "1px solid var(--border-2)",
    whiteSpace: "nowrap",
  };
  const td: React.CSSProperties = {
    padding: "12px",
    borderBottom: "1px solid var(--border-1)",
    fontSize: "var(--fs-body-sm)",
    verticalAlign: "middle",
  };

  return (
    <div className="gf-card" style={{ padding: 0, overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
        <thead>
          <tr>
            <th style={{ ...th, position: "sticky", left: 0, background: "var(--bg-1)" }}>
              Learner
            </th>
            <th style={{ ...th, textAlign: "center" }}>Overall</th>
            {COURSES.map((c) => (
              <th key={c.id} style={{ ...th, textAlign: "center" }} title={c.title}>
                {c.num}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((l) => {
            const overall = overallPct(l.progress);
            return (
              <tr key={l.userId}>
                <td style={{ ...td, position: "sticky", left: 0, background: "var(--bg-1)" }}>
                  <div style={{ fontWeight: 600, color: "var(--fg-1)" }}>{l.name}</div>
                  <div style={{ fontSize: "var(--fs-caption)", color: "var(--fg-3)" }}>
                    {l.email}
                  </div>
                </td>
                <td style={{ ...td, textAlign: "center" }}>
                  <PctBadge value={overall} strong />
                </td>
                {COURSES.map((c) => {
                  const total = STEP_TOTALS[c.id];
                  const done = l.progress.steps[c.id]?.length ?? 0;
                  const quiz = l.progress.quiz[c.id];
                  const courseDone = !!l.progress.completed?.[c.id];
                  return (
                    <td key={c.id} style={{ ...td, textAlign: "center" }}>
                      {total == null ? (
                        <span style={{ color: "var(--fg-3)" }}>—</span>
                      ) : courseDone ? (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontWeight: 700,
                            color: "var(--success)",
                          }}
                          title="Marked complete by the learner"
                        >
                          <I.Check size={13} /> Complete
                        </div>
                      ) : (
                        <>
                          <PctBadge value={pct(done, total)} />
                          {quiz != null && (
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: "var(--fs-caption)",
                                color: "var(--fg-3)",
                              }}
                            >
                              Quiz {Math.round(quiz * 100)}%
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PctBadge({ value, strong }: { value: number; strong?: boolean }) {
  const p = Math.round(value * 100);
  const done = value >= 1;
  const started = value > 0;
  const color = done
    ? "var(--success)"
    : started
      ? "var(--fg-1)"
      : "var(--fg-3)";
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: 40,
        fontWeight: strong ? 700 : 600,
        color,
      }}
    >
      {p}%
    </span>
  );
}
